import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, bg, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/UserAvatar";
import { Loader2, Wifi, History, ShieldAlert, MapPin, Monitor, Smartphone, LogOut, KeyRound, Search } from "lucide-react";
import { RefreshButton } from "@/components/RefreshButton";
import { toast } from "sonner";

import { confirmar } from "@/components/ConfirmDialog";
export const fmtWhen = (iso) => {
  const d = new Date(iso);
  if (!iso || isNaN(d)) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "agora";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400 && new Date().toDateString() === d.toDateString()) return `hoje ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export const DeviceIcon = ({ device, className = "h-4 w-4" }) =>
  /iPhone|Android|Celular/.test(device || "") ? <Smartphone className={className} /> : <Monitor className={className} />;

const Panel = ({ icon: Icon, title, right, children, color = "#232D4F" }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
    <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
      <Icon className="h-4 w-4" style={{ color }} />
      <h2 className="font-display font-semibold text-slate-800 flex-1">{title}</h2>
      {right}
    </div>
    {children}
  </div>
);

const roleTag = (r) => ({ student: "Aluno", instructor: "Professor", admin: "Admin" }[r] || r);

/** Quem está online agora (atualiza sozinho a cada 30 s). */
export function OnlineNow() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/access/online", bg).then((r) => setData(r.data)).catch(() => setData({ count: 0, sessions: [] })), []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const open = (s) => s.role === "student" && navigate(`/admin/alunos?busca=${encodeURIComponent(s.email)}`);

  return (
    <Panel icon={Wifi} color="#1E9E2A" title="Online agora"
      right={<span className="flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A]"><span className="h-2 w-2 rounded-full bg-[#1E9E2A] animate-pulse" />{data ? data.count : "…"}</span>}>
      <div className="max-h-80 overflow-y-auto iep-scroll divide-y divide-slate-100" data-testid="online-now">
        {data === null ? <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : data.sessions.length === 0 ? <p className="p-5 text-sm text-slate-400">Ninguém online neste momento.</p>
          : data.sessions.map((s) => (
            <button key={s.sid} onClick={() => open(s)} className="w-full text-left flex items-center gap-3 px-5 py-3 hover:bg-slate-50">
              <UserAvatar user={s} size={34} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{s.name} <span className="text-[10px] text-slate-400 font-normal ml-1">{roleTag(s.role)}{s.kind === "admin" ? " · via admin" : ""}</span></div>
                <div className="text-xs text-slate-400 flex items-center gap-1 truncate"><MapPin className="h-3 w-3" /> {s.place} · <DeviceIcon device={s.device} className="h-3 w-3" /> {s.device} · {s.browser}</div>
              </div>
              <span className="text-[11px] text-slate-400 shrink-0">{fmtWhen(s.last_seen)}</span>
            </button>
          ))}
      </div>
    </Panel>
  );
}

/** Últimos logins na plataforma. */
export function RecentAccess() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/access/recent", { params: { limit: 25 } }).then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <Panel icon={History} title="Últimos acessos" right={<RefreshButton iconOnly size="sm" onClick={load} done="Acessos atualizados." className="text-slate-400 hover:text-slate-700" />}>
      <div className="max-h-80 overflow-y-auto iep-scroll divide-y divide-slate-100" data-testid="recent-access">
        {rows === null ? <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <p className="p-5 text-sm text-slate-400">Nenhum acesso registrado ainda.</p>
          : rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-2.5">
              <DeviceIcon device={r.device} className="h-4 w-4 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 truncate">{r.name} <span className="text-[10px] text-slate-400 ml-1">{roleTag(r.role)}</span></div>
                <div className={`text-xs truncate ${r.event === "desconectado_limite" ? "text-amber-700" : "text-slate-400"}`}>
                  {r.event === "desconectado_limite" ? "Desconectado por limite de aparelhos · " : ""}{r.place} · {r.device} · {r.browser}
                </div>
              </div>
              <span className="text-[11px] text-slate-400 shrink-0">{fmtWhen(r.at)}</span>
            </div>
          ))}
      </div>
    </Panel>
  );
}

/** Tentativas de login com erro (com o e-mail digitado) — para o suporte entender por que a aluna não entra. */
export function FailedLogins() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/admin/access/failed-logins", { params: { q, hours: 72 } }).then((r) => setRows(r.data)).catch(() => setRows([])), [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const cor = (m) => (m.startsWith("e-mail") ? "text-amber-700" : m.startsWith("conta") ? "text-red-600" : "text-slate-500");
  return (
    <Panel icon={KeyRound} color="#B45309" title="Logins com erro (3 dias)" right={<RefreshButton iconOnly size="sm" onClick={load} done="Atualizado." className="text-slate-400 hover:text-slate-700" />}>
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pelo e-mail digitado" className="h-9 w-full rounded-md border border-slate-200 pl-8 pr-3 text-sm" data-testid="failed-logins-search" />
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto iep-scroll divide-y divide-slate-100 mt-2" data-testid="failed-logins">
        {rows === null ? <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <p className="p-5 text-sm text-slate-400">{q ? "Nenhuma tentativa com esse e-mail. Se a aluna diz que tentou, o pedido não chegou à plataforma (rede/firewall do lugar ou sem internet)." : "Nenhum login com erro."}</p>
          : rows.map((r, i) => (
            <div key={i} className="px-5 py-2.5">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-800 truncate flex-1">{r.email}</span>
                <span className="text-[11px] text-slate-400 shrink-0">{fmtWhen(r.at)}</span>
              </div>
              <div className="text-xs truncate"><span className={`font-semibold ${cor(r.motivo)}`}>{r.motivo}</span><span className="text-slate-400"> · {r.place} · {r.device} · {r.browser}</span></div>
            </div>
          ))}
      </div>
    </Panel>
  );
}

/** Alunos com sinais de compartilhamento de conta. */
export function AccessAlerts() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/access/alerts").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <Panel icon={ShieldAlert} color="#B45309" title="Alertas de compartilhamento (7 dias)"
      right={rows && rows.length > 0 && <span className="text-xs font-bold text-white bg-amber-600 rounded-full px-2 py-0.5">{rows.length}</span>}>
      <div className="max-h-80 overflow-y-auto iep-scroll divide-y divide-slate-100" data-testid="access-alerts">
        {rows === null ? <div className="p-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : rows.length === 0 ? <p className="p-5 text-sm text-slate-400">Nenhum sinal de compartilhamento. 👍</p>
          : rows.map((a) => (
            <button key={a.user_id} onClick={() => a.student_id && navigate(`/admin/alunos/${a.student_id}#acessos`)} className="w-full text-left px-5 py-3 hover:bg-slate-50">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${a.level === "alto" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{a.level}</span>
                <span className="text-sm font-medium text-slate-800 truncate">{a.name}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{a.flags.join(" · ")}</div>
              <div className="text-[11px] text-slate-400">Estados: {a.regions.join(", ") || "—"} · último: {a.last_place}, {fmtWhen(a.last_at)}</div>
            </button>
          ))}
      </div>
    </Panel>
  );
}

const REASON = { limite: "limite de aparelhos", admin: "encerrada pelo admin", logout: "saiu", usuario: "encerrada pelo aluno", fim_admin: "fim do acesso do admin" };

/** Sessões ativas + histórico (admin, na página do aluno/professor). */
export function PersonAccess({ kind, id }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/admin/people/${kind}/${id}/access`).then((r) => setData(r.data)).catch(() => setData(null)), [kind, id]);
  useEffect(() => { load(); }, [load]);

  const endOne = async (sid) => {
    try { await api.post(`/admin/sessions/${sid}/end`); toast.success("Sessão encerrada."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const endAll = async () => {
    if (!(await confirmar("Desconectar esta pessoa de todos os aparelhos?"))) return;
    try { const r = await api.post(`/admin/people-sessions/${kind}/${id}/end`); toast.success(`${r.data.ended} sessão(ões) encerrada(s).`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const sm = data.summary;
  return (
    <div className="space-y-6">
      {sm.flags.length > 0 && (
        <div className={`rounded-xl border p-4 ${sm.level === "alto" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-2 font-semibold text-slate-800"><ShieldAlert className="h-4 w-4 text-amber-700" /> Possível compartilhamento de conta ({sm.level})</div>
          <ul className="text-sm text-slate-600 mt-1 list-disc pl-5">{sm.flags.map((f) => <li key={f}>{f}</li>)}</ul>
        </div>
      )}
      {(sm.notes || []).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="access-notes">
          <div className="font-semibold text-slate-700 text-sm">Para interpretar o histórico</div>
          <ul className="text-sm text-slate-600 mt-1 list-disc pl-5">{sm.notes.map((n) => <li key={n}>{n}</li>)}</ul>
          {sm.flags.length === 0 && <p className="text-xs text-slate-500 mt-2">Nenhum sinal de outra pessoa usando a conta: o alerta só aparece com aparelhos diferentes em lugares distantes.</p>}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Estados (7 dias)", sm.regions.length], ["Aparelhos (7 dias)", sm.devices], ["IPs (7 dias)", sm.ips], ["Desconexões por limite", sm.kicks]].map(([l, v]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-200 p-4"><div className="font-display text-2xl font-bold text-slate-900">{v}</div><div className="text-xs text-slate-500">{l}</div></div>
        ))}
      </div>

      <Panel icon={Wifi} color="#1E9E2A" title={`Conectado agora (limite: ${data.limit} aparelhos)`}
        right={data.sessions.length > 0 && <Button size="sm" variant="outline" className="text-red-600" onClick={endAll}><LogOut className="h-4 w-4 mr-1.5" /> Desconectar de todos</Button>}>
        <div className="divide-y divide-slate-100">
          {data.sessions.length === 0 ? <p className="p-5 text-sm text-slate-400">Nenhuma sessão aberta.</p> : data.sessions.map((s) => (
            <div key={s.sid} className="flex items-center gap-3 px-5 py-3 flex-wrap">
              <DeviceIcon device={s.device} className="h-5 w-5 text-slate-500" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800">{s.device} · {s.browser} {s.kind === "admin" && <span className="text-[10px] text-amber-700 ml-1">(via admin)</span>}</div>
                <div className="text-xs text-slate-400">{s.place}{s.approx ? " (aproximado)" : ""}{s.operator ? ` · ${s.operator}` : ""} · IP {s.ip} · desde {fmtWhen(s.created_at)} · ativo {fmtWhen(s.last_seen)}</div>
              </div>
              {s.online && <span className="text-[11px] font-semibold text-[#1E9E2A]">● online</span>}
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => endOne(s.sid)}>Encerrar</Button>
            </div>
          ))}
        </div>
      </Panel>

      <Panel icon={History} title="Histórico de acessos">
        <div className="overflow-x-auto iep-scroll">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500 bg-slate-50/60">
              <th className="px-5 py-2 font-medium">Quando</th><th className="px-3 py-2 font-medium">Evento</th>
              <th className="px-3 py-2 font-medium">Local</th><th className="px-3 py-2 font-medium">Aparelho</th><th className="px-3 py-2 font-medium">IP</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.history.length === 0 ? <tr><td colSpan={5} className="px-5 py-4 text-slate-400">Nenhum acesso registrado ainda.</td></tr>
                : data.history.map((h, i) => (
                  <tr key={i}>
                    <td className="px-5 py-2 whitespace-nowrap text-slate-600">{new Date(h.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className={`px-3 py-2 ${h.event === "desconectado_limite" ? "text-amber-700" : "text-slate-700"}`}>{h.event_label}</td>
                    <td className="px-3 py-2 text-slate-600">{h.place}
                      {h.operator && <div className="text-[11px] text-slate-400">{h.operator}{h.approx && <span className="ml-1 text-amber-700" title="Internet de operadora nacional (4G/fibra): o IP aparece na cidade da central da operadora, não onde a pessoa está.">· local aproximado</span>}</div>}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{h.device} · {h.browser}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono text-xs">{h.ip}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <p className="text-[11px] text-slate-400">Localização aproximada pelo IP (base DB-IP, db-ip.com). Operadoras de celular podem aparecer em outra cidade do mesmo estado.</p>
    </div>
  );
}

/** Aparelhos conectados do próprio usuário (Minha conta). */
export function MySessions() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/me/sessions").then((r) => setData(r.data)).catch(() => setData({ sessions: [] })), []);
  useEffect(() => { load(); }, [load]);
  const end = async (sid) => {
    try { await api.post(`/me/sessions/${sid}/end`); toast.success("Aparelho desconectado."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return null;
  return (
    <Panel icon={Monitor} title={`Aparelhos conectados${data.limit ? ` (máximo ${data.limit})` : ""}`}>
      <div className="divide-y divide-slate-100">
        {data.sessions.length === 0 ? <p className="p-5 text-sm text-slate-400">Nenhum aparelho conectado.</p> : data.sessions.map((s) => (
          <div key={s.sid} className="flex items-center gap-3 px-5 py-3 flex-wrap">
            <DeviceIcon device={s.device} className="h-5 w-5 text-slate-500" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800">{s.device} · {s.browser} {s.sid === data.current && <span className="text-[11px] text-[#1E9E2A] ml-1">(este aparelho)</span>}</div>
              <div className="text-xs text-slate-400">{s.place} · ativo {fmtWhen(s.last_seen)}</div>
            </div>
            {s.sid !== data.current && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => end(s.sid)}>Desconectar</Button>}
          </div>
        ))}
      </div>
      {data.limit && <p className="px-5 pb-4 text-xs text-slate-400">Ao entrar em um novo aparelho além do limite, o mais antigo é desconectado automaticamente.</p>}
    </Panel>
  );
}
